import { useEffect, useMemo, useRef, useState } from 'react';
import type { ProjectEntity, TagEntity } from '../../../entities/task/model/task.types';
import type { TaskPriority } from '../../../shared/lib/datetime';
import { PRIORITY_ORDER, PRIORITY_UI_LABEL, priorityLabel } from '../../../shared/lib/priority';
import { Button } from '../../../shared/ui/Button';
import { TagPicker } from './TagPicker';
import { formatQuickDateLabel, parseQuickAdd } from '../lib/parseQuickAdd';
import { useTaskActions } from '../lib/useTaskActions';
import { useUserDates } from '../lib/useUserDates';

export type QuickAddDefaultDate = 'today' | 'tomorrow' | 'none';

export interface QuickAddBarProps {
  defaultDate: QuickAddDefaultDate;
  allTags: TagEntity[];
  allProjects?: ProjectEntity[];
  /** Не показывать пункт «Без даты» (для Сегодня). */
  hideNoDate?: boolean;
  /**
   * Жёсткая дата YYYY-MM-DD (календарь). Приоритетнее defaultDate.
   * Кнопка даты показывает эту дату, меню скрыто.
   */
  forcedDate?: string | null;
  /**
   * Жёсткий project_id (экран проекта). Кнопка проекта блокируется.
   */
  forcedProjectId?: string | null;
  onCreateTag?: (name: string, color: string) => Promise<string | null>;
}

type DateMode = 'default' | 'none' | 'today' | 'tomorrow' | 'custom';
type PriorityMode = 'from-text' | TaskPriority;

const DATE_MODES: DateMode[] = ['today', 'tomorrow', 'none', 'custom'];

/**
 * Быстрое добавление: строка + теги/проект/приоритет/дата + умный разбор с превью.
 * Приоритет, дата, проект — expand-меню (клик разворачивает список, выбор сворачивает).
 */
export function QuickAddBar({
  defaultDate,
  allTags,
  allProjects = [],
  hideNoDate = false,
  forcedDate = null,
  forcedProjectId = null,
  onCreateTag,
}: QuickAddBarProps) {
  const { today, tomorrow, timezone } = useUserDates();
  const { createQuickTask } = useTaskActions();
  const [text, setText] = useState('');
  const [dateMode, setDateMode] = useState<DateMode>('default');
  const [customDate, setCustomDate] = useState<string | null>(null);
  const [priorityMode, setPriorityMode] = useState<PriorityMode>('from-text');
  const [manualTagIds, setManualTagIds] = useState<string[]>([]);
  const [manualProjectId, setManualProjectId] = useState<string | null>(null);
  const [showTags, setShowTags] = useState(false);
  const [showPriorityMenu, setShowPriorityMenu] = useState(false);
  const [showDateMenu, setShowDateMenu] = useState(false);
  const [showProjectMenu, setShowProjectMenu] = useState(false);
  const [showCustomDate, setShowCustomDate] = useState(false);
  const [creating, setCreating] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const customDateRef = useRef<HTMLInputElement | null>(null);

  const defaultDueDate =
    forcedDate !== null && forcedDate.length > 0
      ? forcedDate
      : defaultDate === 'today'
        ? today
        : defaultDate === 'tomorrow'
          ? tomorrow
          : null;

  const parsed = useMemo(
    () =>
      parseQuickAdd(text, {
        today,
        tomorrow,
        defaultDueDate,
        timezone,
      }),
    [text, today, tomorrow, defaultDueDate, timezone],
  );

  const effectiveDate = useMemo(() => {
    if (forcedDate !== null && forcedDate.length > 0 && dateMode === 'default' && customDate === null) {
      return forcedDate;
    }
    if (dateMode === 'none') {
      return null;
    }
    if (dateMode === 'today') {
      return today;
    }
    if (dateMode === 'tomorrow') {
      return tomorrow;
    }
    if (dateMode === 'custom' && customDate !== null) {
      return customDate;
    }
    if (customDate !== null && dateMode === 'default') {
      return customDate;
    }
    return parsed.dueDate;
  }, [forcedDate, dateMode, customDate, parsed.dueDate, today, tomorrow]);

  const effectiveProjectId: string | null =
    forcedProjectId !== null && forcedProjectId.length > 0
      ? forcedProjectId
      : manualProjectId;

  const effectivePriority: TaskPriority =
    priorityMode === 'from-text' ? (parsed.priority ?? 'medium') : priorityMode;

  const effectiveTime = effectiveDate === null ? null : parsed.dueTime;

  const previewChips = useMemo(() => {
    const chips: string[] = [];
    chips.push(formatQuickDateLabel(effectiveDate, timezone));
    if (effectiveTime !== null) {
      chips.push(effectiveTime);
    }
    if (effectivePriority !== 'medium' || parsed.hasPriorityToken) {
      chips.push(`!${priorityLabel(effectivePriority)}`);
    }
    if (effectiveProjectId !== null) {
      const project = allProjects.find((item) => item.id === effectiveProjectId);
      if (project !== undefined) {
        chips.push(`#${project.name}`);
      }
    }
    for (const name of parsed.tagNames) {
      chips.push(`#${name}`);
    }
    for (const id of manualTagIds) {
      const tag = allTags.find((item) => item.id === id);
      if (tag !== undefined && !parsed.tagNames.some((n) => n.toLowerCase() === tag.name.toLowerCase())) {
        chips.push(`#${tag.name}`);
      }
    }
    return chips;
  }, [
    effectiveDate,
    effectiveTime,
    effectivePriority,
    effectiveProjectId,
    parsed,
    manualTagIds,
    allTags,
    allProjects,
    timezone,
  ]);

  const titleReady = parsed.title.trim().length > 0;

  const priorityButtonLabel =
    priorityMode === 'from-text'
      ? parsed.priority !== null
        ? `${PRIORITY_UI_LABEL[parsed.priority]} (из текста)`
        : 'Приоритет: из текста'
      : `Приоритет: ${PRIORITY_UI_LABEL[priorityMode]}`;

  const dateButtonLabel =
    forcedDate !== null && forcedDate.length > 0
      ? formatQuickDateLabel(forcedDate, timezone)
      : formatQuickDateLabel(effectiveDate, timezone);

  const projectButtonLabel =
    effectiveProjectId !== null
      ? (allProjects.find((item) => item.id === effectiveProjectId)?.name ?? 'Проект')
      : 'Проект';

  const lockDate = forcedDate !== null && forcedDate.length > 0;
  const lockProject = forcedProjectId !== null && forcedProjectId.length > 0;

  function selectDateMode(mode: Exclude<DateMode, 'custom'>): void {
    setDateMode(mode);
    setShowDateMenu(false);
    setShowCustomDate(false);
    if (mode === 'default') {
      setCustomDate(null);
    }
  }

  function selectPriority(next: PriorityMode): void {
    setPriorityMode(next);
    setShowPriorityMenu(false);
  }

  async function handleSubmit(): Promise<void> {
    if (!titleReady || creating) {
      return;
    }
    setCreating(true);
    try {
      const manualNames: string[] = [];
      const tagIds: string[] = [];
      for (const id of manualTagIds) {
        const tag = allTags.find((item) => item.id === id);
        if (tag === undefined) {
          continue;
        }
        if (parsed.tagNames.some((n) => n.toLowerCase() === tag.name.toLowerCase())) {
          continue;
        }
        if (allTags.some((t) => t.id === id)) {
          tagIds.push(id);
        }
        manualNames.push(tag.name);
      }
      const ok = await createQuickTask({
        title: parsed.title,
        priority: effectivePriority,
        dueDate: effectiveDate,
        dueTime: effectiveTime,
        projectId: effectiveProjectId,
        tagNames: [...parsed.tagNames, ...manualNames.filter((name) => !parsed.tagNames.some((n) => n.toLowerCase() === name.toLowerCase()))],
        tagIds,
      });
      if (ok) {
        setText('');
        setManualTagIds([]);
        setDateMode('default');
        setCustomDate(null);
        setPriorityMode('from-text');
        setManualProjectId(null);
        setShowTags(false);
        setShowPriorityMenu(false);
        setShowDateMenu(false);
        setShowProjectMenu(false);
        setShowCustomDate(false);
        inputRef.current?.focus();
      }
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="tg-card space-y-2 p-3">
      <div className="flex items-start gap-2">
        <input
          ref={inputRef}
          className="tg-input flex-1"
          value={text}
          placeholder="Задача… напр. завтра 18:00 Купить молоко #дом !высокий"
          aria-label="Быстрое добавление задачи"
          enterKeyHint="done"
          onChange={(event) => {
            setText(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              void handleSubmit();
            }
          }}
        />
        <Button
          type="button"
          onClick={() => void handleSubmit()}
          disabled={!titleReady || creating}
          loading={creating}
        >
          Добавить
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          className={`rounded-full border px-2.5 py-1 text-xs ${
            showTags
              ? 'border-tg-accent/50 bg-tg-accent-soft text-tg-accent'
              : 'border-tg-border-soft bg-tg-surface text-tg-text-secondary hover:text-tg-text'
          }`}
          onClick={() => {
            setShowTags((value) => !value);
            setShowPriorityMenu(false);
            setShowDateMenu(false);
            setShowProjectMenu(false);
          }}
          aria-expanded={showTags}
        >
          Теги{manualTagIds.length > 0 ? ` (${manualTagIds.length})` : ''}
        </button>

        <div className="relative">
          {lockProject ? (
            <span className="inline-flex rounded-full border border-tg-accent/40 bg-tg-accent-soft px-2.5 py-1 text-xs text-tg-accent">
              Проект: {projectButtonLabel}
            </span>
          ) : (
            <button
              type="button"
              className={`rounded-full border px-2.5 py-1 text-xs ${
                effectiveProjectId !== null
                  ? 'border-tg-accent/50 bg-tg-accent-soft text-tg-accent'
                  : 'border-tg-border-soft bg-tg-surface text-tg-text-secondary hover:text-tg-text'
              }`}
              onClick={() => {
                setShowProjectMenu((value) => !value);
                setShowPriorityMenu(false);
                setShowDateMenu(false);
                setShowTags(false);
              }}
              aria-expanded={showProjectMenu}
              aria-haspopup="menu"
              title="Проект"
            >
              {projectButtonLabel}
              <span aria-hidden="true" className="ml-1 opacity-70">
                ▼
              </span>
            </button>
          )}

          {showProjectMenu && !lockProject ? (
            <div
              className="absolute left-0 top-full z-30 mt-1 w-52 rounded-xl border border-tg-border-soft bg-tg-surface p-1 shadow-panel"
              role="menu"
              aria-label="Выбор проекта"
            >
              <button
                type="button"
                role="menuitemradio"
                aria-checked={manualProjectId === null}
                className={`block w-full rounded-lg px-2.5 py-2 text-left text-xs ${
                  manualProjectId === null
                    ? 'bg-tg-accent-soft text-tg-accent'
                    : 'text-tg-text-secondary hover:bg-tg-surface-hover hover:text-tg-text'
                }`}
                onClick={() => {
                  setManualProjectId(null);
                  setShowProjectMenu(false);
                }}
              >
                Без проекта
              </button>
              {allProjects.map((project) => (
                <button
                  key={project.id}
                  type="button"
                  role="menuitemradio"
                  aria-checked={manualProjectId === project.id}
                  className={`block w-full rounded-lg px-2.5 py-2 text-left text-xs ${
                    manualProjectId === project.id
                      ? 'bg-tg-accent-soft text-tg-accent'
                      : 'text-tg-text-secondary hover:bg-tg-surface-hover hover:text-tg-text'
                  }`}
                  onClick={() => {
                    setManualProjectId(project.id);
                    setShowProjectMenu(false);
                  }}
                >
                  <span
                    className="mr-1.5 inline-block h-2 w-2 rounded-full align-middle"
                    style={{ backgroundColor: project.color }}
                    aria-hidden="true"
                  />
                  {project.name}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <div className="relative">
          <button
            type="button"
            className={`rounded-full border px-2.5 py-1 text-xs ${
              priorityMode !== 'from-text' || parsed.priority !== null
                ? 'border-tg-accent/50 bg-tg-accent-soft text-tg-accent'
                : 'border-tg-border-soft bg-tg-surface text-tg-text-secondary hover:text-tg-text'
            }`}
            onClick={() => {
              setShowPriorityMenu((value) => !value);
              setShowDateMenu(false);
              setShowTags(false);
              setShowProjectMenu(false);
            }}
            aria-expanded={showPriorityMenu}
            aria-haspopup="menu"
            title="Приоритет"
          >
            {priorityButtonLabel}
            <span aria-hidden="true" className="ml-1 opacity-70">
              ▼
            </span>
          </button>
          {showPriorityMenu ? (
            <div
              className="absolute left-0 top-full z-30 mt-1 w-48 rounded-xl border border-tg-border-soft bg-tg-surface p-1 shadow-panel"
              role="menu"
              aria-label="Выбор приоритета"
            >
              <button
                type="button"
                role="menuitemradio"
                aria-checked={priorityMode === 'from-text'}
                className={`block w-full rounded-lg px-2.5 py-2 text-left text-xs ${
                  priorityMode === 'from-text'
                    ? 'bg-tg-accent-soft text-tg-accent'
                    : 'text-tg-text-secondary hover:bg-tg-surface-hover hover:text-tg-text'
                }`}
                onClick={() => {
                  selectPriority('from-text');
                }}
              >
                из текста
                {parsed.priority !== null ? ` · ${PRIORITY_UI_LABEL[parsed.priority]}` : ''}
              </button>
              <div className="my-1 border-t border-tg-border-soft/60" />
              {PRIORITY_ORDER.map((value) => (
                <button
                  key={value}
                  type="button"
                  role="menuitemradio"
                  aria-checked={priorityMode === value}
                  className={`block w-full rounded-lg px-2.5 py-2 text-left text-xs ${
                    priorityMode === value
                      ? 'bg-tg-accent-soft text-tg-accent'
                      : 'text-tg-text-secondary hover:bg-tg-surface-hover hover:text-tg-text'
                  }`}
                  onClick={() => {
                    selectPriority(value);
                  }}
                >
                  {PRIORITY_UI_LABEL[value]}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <div className="relative">
          {lockDate ? (
            <span className="inline-flex rounded-full border border-tg-accent/40 bg-tg-accent-soft px-2.5 py-1 text-xs text-tg-accent">
              Дата: {formatQuickDateLabel(forcedDate, timezone)}
            </span>
          ) : (
            <button
              type="button"
              className={`rounded-full border px-2.5 py-1 text-xs ${
                dateMode !== 'default' || customDate !== null
                  ? 'border-tg-accent/50 bg-tg-accent-soft text-tg-accent'
                  : 'border-tg-border-soft bg-tg-surface text-tg-text-secondary hover:text-tg-text'
              }`}
              onClick={() => {
                setShowDateMenu((value) => !value);
                setShowPriorityMenu(false);
                setShowTags(false);
                setShowProjectMenu(false);
                setShowCustomDate(false);
              }}
              aria-expanded={showDateMenu}
              aria-haspopup="menu"
            >
              {dateButtonLabel === 'Без даты' ? 'Без даты' : `Дата: ${dateButtonLabel}`}
              <span aria-hidden="true" className="ml-1 opacity-70">
                ▼
              </span>
            </button>
          )}

          {showDateMenu && !lockDate ? (
            <div
              className="absolute left-0 top-full z-30 mt-1 w-52 rounded-xl border border-tg-border-soft bg-tg-surface p-1 shadow-panel"
              role="menu"
              aria-label="Выбор даты"
            >
              <button
                type="button"
                role="menuitem"
                className={`block w-full rounded-lg px-2.5 py-2 text-left text-xs ${
                  dateMode === 'today'
                    ? 'bg-tg-accent-soft text-tg-accent'
                    : 'text-tg-text-secondary hover:bg-tg-surface-hover hover:text-tg-text'
                }`}
                onClick={() => {
                  selectDateMode('today');
                }}
              >
                Сегодня
              </button>
              <button
                type="button"
                role="menuitem"
                className={`block w-full rounded-lg px-2.5 py-2 text-left text-xs ${
                  dateMode === 'tomorrow'
                    ? 'bg-tg-accent-soft text-tg-accent'
                    : 'text-tg-text-secondary hover:bg-tg-surface-hover hover:text-tg-text'
                }`}
                onClick={() => {
                  selectDateMode('tomorrow');
                }}
              >
                Завтра
              </button>
              {!hideNoDate ? (
                <button
                  type="button"
                  role="menuitem"
                  className={`block w-full rounded-lg px-2.5 py-2 text-left text-xs ${
                    dateMode === 'none'
                      ? 'bg-tg-accent-soft text-tg-accent'
                      : 'text-tg-text-secondary hover:bg-tg-surface-hover hover:text-tg-text'
                  }`}
                  onClick={() => {
                    selectDateMode('none');
                  }}
                >
                  Без даты
                </button>
              ) : null}
              <div className="my-1 border-t border-tg-border-soft/60" />
              <button
                type="button"
                role="menuitem"
                aria-expanded={showCustomDate}
                className={`block w-full rounded-lg px-2.5 py-2 text-left text-xs ${
                  dateMode === 'custom' || showCustomDate
                    ? 'bg-tg-accent-soft text-tg-accent'
                    : 'text-tg-text-secondary hover:bg-tg-surface-hover hover:text-tg-text'
                }`}
                onClick={() => {
                  setShowCustomDate(true);
                  window.setTimeout(() => {
                    customDateRef.current?.focus();
                  }, 0);
                }}
              >
                Выбрать дату…
              </button>
              {showCustomDate ? (
                <div className="px-1 pb-1">
                  <input
                    ref={customDateRef}
                    type="date"
                    className="w-full rounded-lg border border-tg-border-soft bg-tg-bg px-2 py-1.5 text-xs text-tg-text"
                    aria-label="Своя дата"
                    value={customDate ?? ''}
                    onChange={(event) => {
                      const value = event.target.value;
                      if (value.length === 0) {
                        setCustomDate(null);
                        setDateMode('default');
                      } else {
                        setCustomDate(value);
                        setDateMode('custom');
                        setShowDateMenu(false);
                        setShowCustomDate(false);
                      }
                    }}
                  />
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        {showTags ? (
          <div className="w-full">
            <TagPicker
              allTags={allTags}
              selectedIds={manualTagIds}
              onChange={setManualTagIds}
              onCreateTag={onCreateTag}
              compact
            />
          </div>
        ) : null}
      </div>

      {text.trim().length > 0 ? (
        <div className="rounded-xl border border-tg-border-soft bg-tg-bg px-3 py-2 text-xs text-tg-text-secondary">
          <span className="text-tg-text-muted">Превью: </span>
          <span className="text-tg-text">{parsed.title || '—'}</span>
          <span className="mx-2 text-tg-text-muted">·</span>
          {previewChips.map((chip) => (
            <span
              key={chip}
              className="mr-1 inline-flex rounded-full border border-tg-accent/30 bg-tg-accent-soft px-2 py-0.5 text-tg-accent"
            >
              {chip}
            </span>
          ))}
          <div className="mt-1 text-tg-text-muted">
            Enter — добавить. Разбор: дата, время, #тег, !приоритет (!urgent / !срочный / !высокий).
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Сброс даты/проекта при смене экрана — через key в родителе. */
export function useQuickAddResetKey(defaultDate: QuickAddDefaultDate): string {
  const [key, setKey] = useState(defaultDate);
  useEffect(() => {
    setKey(defaultDate);
  }, [defaultDate]);
  return key;
}

export { DATE_MODES };
